import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import { usePopup } from '../context/PopupContext';
import { useTour } from '../context/TourContext';
import { isAdmin } from '../../util/auth';
import { isRouteActive } from '../../util/routeUtils';
import { ROUTES } from '../../util/routes';
import { useLogout } from '../../hooks/useLogout';
import NavBadge from '../input/NavBadge';
import { homeTourSteps, HOME_TOUR_KEY } from '../tour/tours/homeTour';
import { upcomingEventsTourSteps, UPCOMING_EVENTS_TOUR_KEY } from '../tour/tours/upcomingEventsTour';
import { eventTourSteps, EVENT_TOUR_KEY } from '../tour/tours/eventTour';
import './BottomNav.css';

const NAV_ITEMS = [
  { icon: 'home', label: 'Home', to: ROUTES.HOME, exact: true, tourTarget: 'nav-home-mobile' },
  { icon: 'event', label: 'Events', to: ROUTES.UPCOMING_EVENTS, tourTarget: 'nav-events-mobile' },
  { icon: 'inbox', label: 'Inbox', to: ROUTES.REGISTRATIONS, adminOnly: true },
  { icon: 'person', label: 'Spieler', to: ROUTES.USERS, adminOnly: true },
  { icon: 'query_stats', label: 'Statistiken', to: ROUTES.STATISTICS, tourTarget: 'nav-statistics-mobile' },
];

function BottomNav({ isSubscribed, registrationsBadge }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { openPopup } = usePopup();
  const { openLegend, startTour, resetTour } = useTour();
  const admin = isAdmin();
  const logout = useLogout();
  const [moreOpen, setMoreOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter(item => !item.adminOnly || admin);

  const closeMore = () => setMoreOpen(false);

  const currentPageHasTour = () => {
    const p = location.pathname;
    return p === ROUTES.HOME || p === ROUTES.UPCOMING_EVENTS || p.startsWith(ROUTES.EVENT_BASE + '/');
  };

  const handleStartTour = () => {
    const p = location.pathname;
    if (p === ROUTES.HOME) { resetTour(HOME_TOUR_KEY); startTour(HOME_TOUR_KEY, homeTourSteps); }
    else if (p === ROUTES.UPCOMING_EVENTS) { resetTour(UPCOMING_EVENTS_TOUR_KEY); startTour(UPCOMING_EVENTS_TOUR_KEY, upcomingEventsTourSteps); }
    else if (p.startsWith(ROUTES.EVENT_BASE + '/')) { resetTour(EVENT_TOUR_KEY); startTour(EVENT_TOUR_KEY, eventTourSteps); }
  };

  const handleMoreAction = (fn) => {
    closeMore();
    fn();
  };

  return (
    <>
      <nav className="bottomNav">
        {visibleItems.map(item => (
          <Link
            key={item.to}
            to={item.to}
            className={`bottomNavItem${isRouteActive(location.pathname, item) ? ' active' : ''}`}
            data-tour={item.tourTarget}
          >
            <span className="bottomNavIconWrapper">
              <span className={`material-symbols-rounded bottomNavIcon`}>{item.icon}</span>
              {item.to === ROUTES.REGISTRATIONS && registrationsBadge > 0 && (
                <NavBadge count={registrationsBadge} size={14} fontSize={9} />
              )}
            </span>
            <span className="bottomNavLabel">{item.label}</span>
          </Link>
        ))}
        <button
          className={`bottomNavItem${moreOpen ? ' active' : ''}`}
          onClick={() => setMoreOpen(o => !o)}
          data-tour="nav-settings-mobile"
        >
          <span className="material-symbols-rounded bottomNavIcon">more_horiz</span>
          <span className="bottomNavLabel">Mehr</span>
        </button>
      </nav>

      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              className="mobileMenuOverlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMore}
            />
            <motion.div
              className="mobileMenuPanel"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <div className="mobileMenuHandle" />

              <button className="mobileMenuItem" onClick={() => handleMoreAction(toggleDarkMode)}>
                <span className="material-symbols-rounded">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>

              <button className="mobileMenuItem" onClick={() => handleMoreAction(() => navigate(ROUTES.DOCS_ORGA))}>
                <span className="material-symbols-rounded">menu_book</span>
                Orga-Doku
              </button>

              {admin && (
                <button className="mobileMenuItem" onClick={() => handleMoreAction(() => navigate(ROUTES.DOCS_ADMIN))}>
                  <span className="material-symbols-rounded">shield_person</span>
                  Admin-Doku
                </button>
              )}

              <button className="mobileMenuItem" onClick={() => handleMoreAction(openLegend)}>
                <span className="material-symbols-rounded">legend_toggle</span>
                Icon-Legende
              </button>

              {currentPageHasTour() && (
                <button className="mobileMenuItem" onClick={() => handleMoreAction(handleStartTour)}>
                  <span className="material-symbols-rounded">map</span>
                  Rundtour starten
                </button>
              )}

              <button className="mobileMenuItem" onClick={() => handleMoreAction(() => openPopup('pushnotifications', 0))}>
                <span className="material-symbols-rounded">{isSubscribed ? 'notifications_active' : 'notifications'}</span>
                Benachrichtigungen
              </button>

              <button className="mobileMenuItem" onClick={() => handleMoreAction(() => openPopup('editpassword', 0))}>
                <span className="material-symbols-rounded">lock</span>
                Passwort ändern
              </button>

              <div className="mobileMenuDivider" />

              <button className="mobileMenuItem danger" onClick={() => handleMoreAction(logout)}>
                <span className="material-symbols-rounded">logout</span>
                Ausloggen
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default BottomNav;
